// frontend/src/pages/Drift.js
// Read-only inventory drift audit. Lists SKUs where channel-side qty (last seen
// on listing_sync) doesn't match our master qty. Oversell rows are highlighted —
// those mean a channel thinks more is available than we have, i.e. a buyer
// could check out and we'd have nothing to ship.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, channelLabel } from '../App';

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' });
}

export default function Drift() {
  const [data, setData]     = useState(null);
  const [filter, setFilter] = useState('oversell');   // 'oversell' | 'all' | 'undersell'
  const [channel, setChannel] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  function load() {
    setLoading(true);
    api.get('/inventory/drift').then(r => setData(r.data)).finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  if (!data) return <div className="loading">Loading drift audit…</div>;

  const rows = (data.rows || []).filter(r => {
    if (channel !== 'all' && r.channel !== channel) return false;
    if (filter === 'oversell'  && r.delta_qty <= 0) return false;
    if (filter === 'undersell' && r.delta_qty >= 0) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!(r.sku || '').toLowerCase().includes(q) &&
          !(r.title || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const t = data.totals || {};
  const channels = Object.keys(t.by_channel || {});

  return (
    <div>
      <div className="page-header">
        <div className="page-title">⚖️ Inventory Drift</div>
        <button className="btn btn-ghost" onClick={load} disabled={loading}>
          {loading ? '⏳ Refreshing…' : '🔄 Refresh'}
        </button>
      </div>

      <div className="dash-kpi-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card">
          <div className="stat-value" style={{ color: t.oversell > 0 ? 'var(--danger)' : 'var(--accent2)' }}>{t.oversell ?? 0}</div>
          <div className="stat-label">Oversell-risk rows</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--warn)' }}>{t.undersell ?? 0}</div>
          <div className="stat-label">Undersell rows</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{t.total ?? 0}</div>
          <div className="stat-label">Total drifted rows</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{Object.keys(t.by_channel || {}).length}</div>
          <div className="stat-label">Channels affected</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--muted)', marginRight: 4 }}>View:</span>
          {['oversell', 'all', 'undersell'].map(f => (
            <button key={f} className={`inv-filter-btn ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}>
              {f === 'oversell' ? '⚠️ Oversell only' : f === 'undersell' ? 'Undersell only' : 'All drift'}
            </button>
          ))}
          <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 12, marginRight: 4 }}>Channel:</span>
          <button className={`inv-filter-btn ${channel === 'all' ? 'active' : ''}`} onClick={() => setChannel('all')}>All</button>
          {channels.map(c => (
            <button key={c} className={`inv-filter-btn ${channel === c ? 'active' : ''}`} onClick={() => setChannel(c)}>
              {channelLabel(c)} ({t.by_channel[c].count}{t.by_channel[c].oversell > 0 ? ` / ⚠️${t.by_channel[c].oversell}` : ''})
            </button>
          ))}
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Filter by SKU or title…"
            style={{ flex: 1, minWidth: 200, marginLeft: 'auto', maxWidth: 320 }} />
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✓</div>
          {filter === 'oversell'
            ? 'No oversell-risk drift. Master qty ≥ what each channel believes.'
            : 'No drift matching this filter.'}
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Title</th>
                  <th>Channel</th>
                  <th style={{ textAlign: 'right' }}>Master</th>
                  <th style={{ textAlign: 'right' }}>Channel</th>
                  <th style={{ textAlign: 'right' }}>Δ</th>
                  <th>Last sync</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const isOver = r.delta_qty > 0;
                  return (
                    <tr key={i} style={isOver ? { background: 'rgba(255,77,109,.06)' } : undefined}>
                      <td>
                        <Link to={`/inventory?search=${encodeURIComponent(r.sku)}`} className="mono"
                          style={{ color: 'var(--accent2)', textDecoration: 'none' }}>
                          {r.sku}
                        </Link>
                      </td>
                      <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.title || '—'}
                      </td>
                      <td>
                        <span className="badge" style={{ background: 'var(--surface2)', color: 'var(--text2)' }}>
                          {channelLabel(r.channel)}
                        </span>
                        {/* An eBay markdown/Sale promo is running on this listing. eBay
                           rejects price revisions while it does (error 21919248), so the
                           channel price will sit under master until the sale ends. */}
                        {!!r.on_sale && (
                          <span className="badge"
                            title={`On sale: now $${r.channel_price != null ? parseFloat(r.channel_price).toFixed(2) : '?'}`
                              + `, was $${r.sale_original_price != null ? parseFloat(r.sale_original_price).toFixed(2) : '?'}`}
                            style={{ marginLeft: 6, background: 'rgba(255,214,10,.15)', color: 'var(--warn)',
                              fontWeight: 800, cursor: 'help' }}>
                            ON SALE
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{r.master_qty}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{r.channel_qty}</td>
                      <td style={{
                        textAlign: 'right', fontWeight: 700,
                        color: isOver ? 'var(--danger)' : 'var(--warn)',
                      }}>
                        {r.delta_qty > 0 ? `+${r.delta_qty}` : r.delta_qty}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{fmtTime(r.last_synced_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 12, padding: '0 4px' }}>
        Δ = channel_qty − master_qty. Positive Δ = channel believes there's more available than we have (oversell risk).
        Master is authoritative; the hourly inventory_push job pushes master qty to channels for any drifted rows.
        {' '}Rows tagged ON SALE have an eBay markdown promo running — eBay rejects price revisions on those
        (error 21919248), so their channel price stays below master until the sale ends. Quantity still syncs.
      </div>
    </div>
  );
}
